import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  ArrowLeftCircle,
  ArrowRightCircle,
  Barcode,
  CheckCircle2,
  ClipboardList,
  History,
  Info,
  PlayCircle,
  PlusCircle,
  Printer,
  RotateCcw,
  Search,
  XCircle,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card, CardHead } from '@/Components/ui/Card';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import { EmptyState } from '@/Components/data/EmptyState';
import { StatusBadge } from '@/Components/ui/Badge';
import type { Tone } from '@/Components/ui/types';
import rastreabilidadeService, {
  type RastreabilidadeDashboard,
  type RastreabilidadeReferencia,
  type RastreabilidadeResultado,
} from '@/services/rastreabilidade';

const TIPOS_BUSCA = [
  { value: '', label: 'Todos' },
  { value: 'LOTE', label: 'Lote Produto Acabado' },
  { value: 'LOTE_MP', label: 'Lote Matéria-Prima' },
  { value: 'OP', label: 'Ordem de Produção' },
  { value: 'NF', label: 'Nota Fiscal' },
];

const PLACEHOLDER_BUSCA: Record<string, string> = {
  LOTE: 'Digite o número do lote do PA',
  LOTE_MP: 'Digite o número do lote da MP',
  OP: 'Digite o número da OP',
  NF: 'Digite o número da NF',
};

const DEFAULT_DASHBOARD: RastreabilidadeDashboard = {
  total: 0,
  lotes_pa: 0,
  lotes_mp: 0,
  ops: 0,
  em_producao: 0,
  livres: 0,
  quarentena: 0,
};

const ICON_MAP: Record<string, typeof PlusCircle> = {
  'ri-add-circle-line': PlusCircle,
  'ri-play-circle-line': PlayCircle,
  'ri-checkbox-circle-line': CheckCircle2,
  'ri-close-circle-line': XCircle,
  'ri-arrow-right-circle-line': ArrowRightCircle,
  'ri-file-add-line': PlusCircle,
};

const STATUS_TONES: Record<string, Tone> = {
  Livre: 'success',
  Bloqueado: 'danger',
  Quarentena: 'warning',
  LIVRE: 'success',
  BLOQUEADO: 'danger',
  QUARENTENA: 'warning',
  'Não encontrado': 'neutral',
  ABERTA: 'neutral',
  LIBERADA: 'info',
  EM_PRODUCAO: 'warning',
  PAUSADA: 'warning',
  ENCERRADA: 'success',
  CANCELADA: 'danger',
};

const STATUS_LABELS: Record<string, string> = {
  LIVRE: 'Livre',
  BLOQUEADO: 'Bloqueado',
  QUARENTENA: 'Quarentena',
  ABERTA: 'Aberta',
  LIBERADA: 'Liberada',
  EM_PRODUCAO: 'Em Produção',
  PAUSADA: 'Pausada',
  ENCERRADA: 'Encerrada',
  CANCELADA: 'Cancelada',
};

function displayStatus(status?: string | null) {
  return status ? STATUS_LABELS[status] || status : '-';
}

function formatNumber(v: number | string | null | undefined) {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function formatDate(v?: string | null) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('pt-BR');
}

function formatDateTime(v?: string | null) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleString('pt-BR');
}

function getErrorMessage(error: unknown) {
  const data = (error as { response?: { data?: { error?: string; message?: string } } }).response?.data;
  return data?.error || data?.message || 'Erro ao buscar rastreabilidade.';
}

export default function RastreabilidadePage() {
  const navigate = useNavigate();
  const [tipoBusca, setTipoBusca] = useState('');
  const [termoBusca, setTermoBusca] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingReferencias, setLoadingReferencias] = useState(false);
  const [referencias, setReferencias] = useState<RastreabilidadeReferencia[]>([]);
  const [dashboard, setDashboard] = useState<RastreabilidadeDashboard>(DEFAULT_DASHBOARD);
  const [referenciaSelecionada, setReferenciaSelecionada] = useState<string | null>(null);
  const [resultado, setResultado] = useState<RastreabilidadeResultado | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const placeholderBusca = useMemo(() => PLACEHOLDER_BUSCA[tipoBusca] || 'Filtrar por lote, OP, produto ou status', [tipoBusca]);

  const carregarReferencias = async (tipo = tipoBusca, termo = termoBusca) => {
    setLoadingReferencias(true);
    setErro(null);
    try {
      const data = await rastreabilidadeService.listarReferencias({
        tipo: tipo || undefined,
        search: termo.trim() || undefined,
      });
      setReferencias(data.items);
      setDashboard(data.dashboard);
    } catch (error) {
      console.error('Erro ao carregar registros rastreáveis', error);
      setErro(getErrorMessage(error));
    } finally {
      setLoadingReferencias(false);
    }
  };

  useEffect(() => {
    void carregarReferencias(tipoBusca, termoBusca);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoBusca]);

  const filtrarReferencias = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    void carregarReferencias();
  };

  const limparFiltros = () => {
    setTipoBusca('');
    setTermoBusca('');
    setReferenciaSelecionada(null);
    setResultado(null);
    void carregarReferencias('', '');
  };

  const buscar = async (tipo: string, termo: string) => {
    const termoNormalizado = termo.trim();
    if (!termoNormalizado) {
      setErro('Selecione um registro para rastrear.');
      return;
    }

    setLoading(true);
    setResultado(null);
    setErro(null);
    try {
      setResultado(await rastreabilidadeService.rastrear(tipo, termoNormalizado));
    } catch (error) {
      console.error('Erro ao rastrear', error);
      setErro(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const selecionarReferencia = (item: RastreabilidadeReferencia) => {
    setReferenciaSelecionada(`${item.tipo}:${item.identificador}`);
    void buscar(item.tipo, item.identificador);
  };

  const rastrearLote = (numeroLote: string, tipo = 'LOTE_MP') => {
    void buscar(tipo, numeroLote);
  };

  const rastrearOP = (numeroOp: string) => {
    void buscar('OP', numeroOp);
  };

  return (
    <div className="nx-page">
      <PageHeader
        icon={<Barcode size={21} strokeWidth={2} />}
        title="Rastreabilidade"
        description="Consulte a origem, produção, qualidade e destino de lotes e ordens de produção."
      />

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead title="Consulta" />
        <form className="nx-rastreabilidade-query-grid" onSubmit={filtrarReferencias}>
          <div className="nx-sngpc-filter-field nx-rastreabilidade-query-type">
            <label htmlFor="rast-tipo">Buscar por</label>
            <Select id="rast-tipo" value={tipoBusca} onChange={(e) => setTipoBusca(e.target.value)}>
              {TIPOS_BUSCA.map((tipo) => (
                <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
              ))}
            </Select>
          </div>
          <div className="nx-sngpc-filter-field nx-rastreabilidade-query-search">
            <label htmlFor="rast-termo">{placeholderBusca}</label>
            <div className="nx-search-field">
              <Search size={15} strokeWidth={2} aria-hidden />
              <input
                id="rast-termo"
                value={termoBusca}
                onChange={(e) => setTermoBusca(e.target.value)}
                placeholder={placeholderBusca}
              />
            </div>
          </div>
          <div className="nx-rastreabilidade-query-actions">
            <Button type="submit" loading={loadingReferencias} loadingText="Filtrando...">
              <Search size={16} strokeWidth={2.2} />
              Filtrar
            </Button>
            <Button type="button" variant="ghost" onClick={limparFiltros}>
              <RotateCcw size={15} />
              Limpar
            </Button>
          </div>
        </form>
      </Card>

      <div className="nx-rastreabilidade-dashboard">
        <Card className="nx-rastreabilidade-metric">
          <span>Total</span>
          <strong>{dashboard.total}</strong>
        </Card>
        <Card className="nx-rastreabilidade-metric">
          <span>Lotes PA</span>
          <strong>{dashboard.lotes_pa}</strong>
        </Card>
        <Card className="nx-rastreabilidade-metric">
          <span>Lotes MP</span>
          <strong>{dashboard.lotes_mp}</strong>
        </Card>
        <Card className="nx-rastreabilidade-metric">
          <span>OPs</span>
          <strong>{dashboard.ops}</strong>
        </Card>
        <Card className="nx-rastreabilidade-metric">
          <span>Em produção</span>
          <strong>{dashboard.em_producao}</strong>
        </Card>
      </div>

      <Card className="nx-page-card nx-sngpc-listing-card">
        <CardHead title="Registros rastreáveis" />
        {loadingReferencias ? (
          <div className="nx-table-loading">Carregando registros...</div>
        ) : referencias.length === 0 ? (
          <EmptyState message="Nenhum registro rastreável encontrado para os filtros informados." />
        ) : (
          <div className="nx-table-wrap">
            <table className="nx-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Identificador</th>
                  <th>Produto</th>
                  <th>Quantidade</th>
                  <th>Data</th>
                  <th>Status</th>
                  <th>Origem</th>
                  <th className="nx-col-acoes">Ações</th>
                </tr>
              </thead>
              <tbody>
                {referencias.map((item) => {
                  const selected = referenciaSelecionada === `${item.tipo}:${item.identificador}`;

                  return (
                    <tr key={`${item.tipo}-${item.id}`} className={selected ? 'nx-rastreabilidade-row--selected' : undefined}>
                      <td>{item.tipo_label}</td>
                      <td>
                        <button type="button" className="nx-rastreabilidade-link" onClick={() => selecionarReferencia(item)}>
                          {item.identificador}
                        </button>
                      </td>
                      <td>
                        <div>{item.produto_nome || '-'}</div>
                        {item.produto_codigo ? <span className="nx-rastreabilidade-muted">{item.produto_codigo}</span> : null}
                      </td>
                      <td>{formatNumber(item.quantidade)}</td>
                      <td>{formatDate(item.data)}</td>
                      <td><StatusBadge label={displayStatus(item.status)} tone={STATUS_TONES[item.status] || 'neutral'} /></td>
                      <td>{item.origem || '-'}</td>
                      <td className="nx-col-acoes">
                        <Button type="button" variant={selected ? 'primary' : 'ghost'} onClick={() => selecionarReferencia(item)} loading={selected && loading} loadingText="Abrindo...">
                          <Search size={15} />
                          Rastrear
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {erro ? (
        <div className="nx-form-alert nx-form-alert--error" role="alert">
          {erro}
        </div>
      ) : null}

      {resultado ? (
        <Card className="nx-page-card nx-sngpc-listing-card">
          <CardHead
            title={`${resultado.identificador} - ${resultado.tipo_label}`}
            actions={
              <Button type="button" variant="ghost" onClick={() => window.print()}>
                <Printer size={16} />
                Imprimir
              </Button>
            }
          />

          <section className="nx-sngpc-form-section">
            <div className="nx-sngpc-form-section__header">
              <h2><Info size={16} /> Informações Gerais</h2>
            </div>
            <div className="nx-detail-grid">
              <div>
                <span className="nx-field-label">Produto</span>
                <div>{resultado.produto_nome}</div>
              </div>
              <div>
                <span className="nx-field-label">Quantidade</span>
                <div>{formatNumber(resultado.quantidade)}</div>
              </div>
              <div>
                <span className="nx-field-label">Data</span>
                <div>{formatDate(resultado.data)}</div>
              </div>
              <div>
                <span className="nx-field-label">Status</span>
                <StatusBadge label={resultado.status} tone={STATUS_TONES[resultado.status] || 'neutral'} />
              </div>
              <div>
                <span className="nx-field-label">Validade</span>
                <div>{formatDate(resultado.validade)}</div>
              </div>
            </div>
          </section>

          <section className="nx-sngpc-form-section">
            <div className="nx-sngpc-form-section__header">
              <h2><History size={16} /> Linha do Tempo</h2>
            </div>
            {resultado.timeline.length === 0 ? (
              <EmptyState message="Sem histórico de eventos." />
            ) : (
              <div className="nx-detail-grid">
                {resultado.timeline.map((evento, index) => {
                  const Icon = ICON_MAP[evento.icon] || Info;
                  return (
                    <div key={`${evento.titulo}-${index}`} className="nx-detail-span">
                      <span className="nx-field-label">{formatDateTime(evento.data)}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 700 }}>
                        <Icon size={16} />
                        {evento.titulo}
                      </div>
                      <div style={{ color: 'var(--nx-muted)', marginTop: 4 }}>{evento.descricao}</div>
                      {evento.link ? (
                        <Button type="button" variant="ghost" onClick={() => navigate(evento.link!)} style={{ marginTop: '0.45rem' }}>
                          Ver detalhes
                        </Button>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {resultado.origem.length > 0 ? (
            <section className="nx-sngpc-form-section">
              <div className="nx-sngpc-form-section__header">
                <h2><ArrowLeftCircle size={16} /> Origem</h2>
              </div>
              <div className="nx-table-wrap">
                <table className="nx-table">
                  <thead>
                    <tr>
                      <th>Lote MP</th>
                      <th>Produto</th>
                      <th>Fornecedor</th>
                      <th>Quantidade</th>
                      <th>Validade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.origem.map((item, index) => (
                      <tr key={`${item.lote_numero}-${index}`}>
                        <td>
                          <button
                            type="button"
                            onClick={() => rastrearLote(item.lote_numero, 'LOTE_MP')}
                            style={{ color: 'var(--nx-primary)', fontWeight: 700 }}
                          >
                            {item.lote_numero}
                          </button>
                        </td>
                        <td>{item.produto_nome}</td>
                        <td>{item.fornecedor}</td>
                        <td>{formatNumber(item.quantidade)}</td>
                        <td>{formatDate(item.validade)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {resultado.destino.length > 0 ? (
            <section className="nx-sngpc-form-section">
              <div className="nx-sngpc-form-section__header">
                <h2><ArrowRightCircle size={16} /> Destino</h2>
              </div>
              <div className="nx-table-wrap">
                <table className="nx-table">
                  <thead>
                    <tr>
                      <th>Cliente/Destino</th>
                      <th>NF/Documento</th>
                      <th>Data</th>
                      <th>Quantidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.destino.map((item, index) => (
                      <tr key={`${item.nf}-${index}`}>
                        <td>{item.cliente}</td>
                        <td>{item.nf}</td>
                        <td>{formatDate(item.data_venda)}</td>
                        <td>{formatNumber(item.quantidade)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}

          {resultado.ops.length > 0 ? (
            <section className="nx-sngpc-form-section">
              <div className="nx-sngpc-form-section__header">
                <h2><ClipboardList size={16} /> Ordens de Produção Relacionadas</h2>
              </div>
              <div className="nx-table-wrap">
                <table className="nx-table">
                  <thead>
                    <tr>
                      <th>Número</th>
                      <th>Produto</th>
                      <th>Status</th>
                      <th>Data Início</th>
                      <th>Quantidade</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultado.ops.map((item, index) => (
                      <tr key={`${item.numero}-${index}`}>
                        <td>
                          <button
                            type="button"
                            onClick={() => rastrearOP(item.numero)}
                            style={{ color: 'var(--nx-primary)', fontWeight: 700 }}
                          >
                            {item.numero}
                          </button>
                        </td>
                        <td>{item.produto_nome}</td>
                        <td><StatusBadge label={item.status} tone={STATUS_TONES[item.status] || 'neutral'} /></td>
                        <td>{formatDate(item.data_inicio)}</td>
                        <td>{formatNumber(item.quantidade)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </Card>
      ) : !loading ? (
        <Card className="nx-page-card nx-sngpc-listing-card">
          <EmptyState message="Selecione um registro da lista para visualizar origem, processos e destino final." />
        </Card>
      ) : null}
    </div>
  );
}
